import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { formatCurrency, isCurrencyAmount, parseEuroAmount } from '@/lib/format';

const pluginKey = new PluginKey('investmentAutoCalc');

export function shouldPreserveManualSubtotal(value: string): boolean {
  const input = value.trim();
  return input !== '' && !isCurrencyAmount(input);
}

/**
 * Auto-calculates subtotal column (col 4) in hourly investment tables.
 *
 * Structure: investmentGroupNode > [investmentHeaderNode, table, investmentTotalNode]+
 * For hourly tables, data rows have 4 cells:
 *   [0] Omschrijving  [1] Uurtarief  [2] Uren  [3] Subtotaal (auto-calculated)
 */
export const InvestmentAutoCalc = Extension.create({
  name: 'investmentAutoCalc',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: pluginKey,
        appendTransaction(transactions, _oldState, newState) {
          // Alleen de eigen correctietransactie overslaan. Undo/redo moet juist
          // opnieuw rekenen, anders kan het aantal uren teruggaan terwijl het
          // subtotaal van de latere waarde zichtbaar blijft.
          if (transactions.some((transaction) => transaction.getMeta(pluginKey))) return null;
          if (!transactions.some((t) => t.docChanged)) return null;

          const { schema } = newState;
          const updates: { from: number; to: number; content: any }[] = [];

          newState.doc.descendants((groupNode, groupPos) => {
            if (groupNode.type.name !== 'investmentGroupNode') return true;

            const invCurrency = (groupNode.attrs?.currency as string) || 'EUR';

            let currentPricingType: string | null = null;

            groupNode.forEach((child, childOffset) => {
              if (child.type.name === 'investmentHeaderNode') {
                currentPricingType = child.attrs.pricingType;
                return;
              }

              if (child.type.name !== 'table' || currentPricingType !== 'hourly') return;

              const tableAbsPos = groupPos + 1 + childOffset;

              child.forEach((row, rowOffset) => {
                if (row.type.name !== 'tableRow' || row.attrs.investmentProduct || row.attrs.productPrice) return;

                let hasHeader = false;
                row.forEach((cell) => {
                  if (cell.type.name === 'tableHeader') hasHeader = true;
                });
                if (hasHeader) return;

                const rowAbsPos = tableAbsPos + 1 + rowOffset;
                const cellInfos: { text: string; contentStart: number; contentEnd: number }[] = [];

                row.forEach((cell, cellOffset) => {
                  if (cell.type.name !== 'tableCell') return;
                  const cellAbsPos = rowAbsPos + 1 + cellOffset;
                  cellInfos.push({
                    text: cell.textContent || '',
                    contentStart: cellAbsPos + 1,
                    contentEnd: cellAbsPos + cell.nodeSize - 1,
                  });
                });

                if (cellInfos.length < 4) return;

                // De subtotaalcel mag bewust vrije tekst bevatten. Zodra er
                // bijvoorbeeld "Op maat" staat, is die handmatige invoer leidend.
                if (shouldPreserveManualSubtotal(cellInfos[3].text)) return;

                // Vrije tekst zoals “Op maat” is een geldige prijs en mag niet
                // door de automatische berekening in € 0,00 worden veranderd.
                if (!isCurrencyAmount(cellInfos[1].text)) {
                  const desired = cellInfos[1].text.trim();
                  const current = cellInfos[3].text.trim();
                  if (current === desired) return;
                  const paragraph = desired
                    ? schema.nodes.paragraph.create(null, schema.text(desired))
                    : schema.nodes.paragraph.create();
                  updates.push({
                    from: cellInfos[3].contentStart,
                    to: cellInfos[3].contentEnd,
                    content: paragraph,
                  });
                  return;
                }

                const price = parseEuroAmount(cellInfos[1].text);
                const hours = parseEuroAmount(cellInfos[2].text);
                const computed = Math.round(price * hours * 100) / 100;
                const currentSub = parseEuroAmount(cellInfos[3].text);
                const desired =
                  computed === 0 ? '' : formatCurrency(computed, invCurrency);
                const currentTrim = cellInfos[3].text.replace(/\s/g, ' ').trim();

                if (
                  Math.abs(computed - currentSub) < 0.00001 &&
                  (desired === '' ? currentTrim === '' : currentTrim === desired)
                ) {
                  return;
                }

                const subtotalStr = desired;
                const newParagraph = subtotalStr
                  ? schema.nodes.paragraph.create(null, schema.text(subtotalStr))
                  : schema.nodes.paragraph.create();

                updates.push({
                  from: cellInfos[3].contentStart,
                  to: cellInfos[3].contentEnd,
                  content: newParagraph,
                });
              });
            });

            return false;
          });

          if (updates.length === 0) return null;

          const { tr } = newState;
          tr.setMeta('addToHistory', false);
          tr.setMeta(pluginKey, true);
          for (let i = updates.length - 1; i >= 0; i--) {
            const u = updates[i];
            tr.replaceWith(u.from, u.to, u.content);
          }
          return tr;
        },
      }),
    ];
  },
});
